import React, {useEffect, useMemo, useRef, useState} from "react";
import {faArrowLeft, faChevronRight, faSave} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import usePreventNavigationContext from "@/services/context/prohibit-nagivation/use-prevent-navigation-context.ts";
import useProjectFormNavigationContext from "@/pages/takeoff/context/project-form-navigation/use-project-form-navigation-context.ts";
import useModelingToolsContext from "../../../../../../services/context/viewer/modeling-tools/use-modeling-tools-context.ts";

import PlanDataDetails, { PlanDataDetailsRef } from "./plan-data.tsx";
import OrganisationPlanViewerContainer, { OrganisationPlanViewerContainerRef } from "./organisation-plan-viewer-container.tsx";
import Button from "../../../../../../components/buttons/button";
import ProjectLockStatus from "../../../../../../models/enums/project-lock-status.ts";
import { useProjectFormHeadingLayoutEffect } from "../../../../context/project-heading/use-project-heading-context.ts";
import useProjectContext from "../../../../../../services/context/project/use-project-context.ts";
import useProjectFilePlanContext from "./context/use-project-file-plan-context.ts";
import ViewerContextProvider from "@/services/context/viewer/instance/viewer-context-provider.tsx";
import useOrganisationPlanLoaders from "./hooks/use-organisation-plan-loaders.ts";
import useOrganisationPlanTools from "./hooks/use-organisation-plan-tools.ts";
import LoadingButton from "@/components/buttons/loading-button";
import {ProjectHeadingState} from "@/pages/takeoff/context/project-heading/project-heading-context-props.ts";
import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";

const ProjectOrganisationPlanDetails: React.FC = () => {
    const { projectLockStatus } = useProjectContext();
    const { building, storey, storeyFile } = useProjectFilePlanContext();
    const { setIsNavigationPrevented, setSaveCallback } = usePreventNavigationContext();

    const { dataContent, isDirty, isSaving, saveCurrentFileData } = useProjectFilePlanContext();
    const { isInDrawingMode } = useModelingToolsContext();

    const { setRoute, getFrom } = useProjectFormNavigationContext();

    const [canSave, setCanSave] = useState(false);

    const planDataDetailsRef = useRef<PlanDataDetailsRef>(null);
    const viewerRef = useRef<OrganisationPlanViewerContainerRef>(null);

    const viewerLoaders = useOrganisationPlanLoaders();
    const viewerTools = useOrganisationPlanTools();

    useEffect(() => {
        setIsNavigationPrevented(isDirty || isSaving);
        return () => setIsNavigationPrevented(
            false
        )
    }, [isDirty, isSaving, setIsNavigationPrevented]);

    useEffect(() => {
        setSaveCallback(saveCurrentFileData);
        return () => {
            setSaveCallback(undefined);
        };
    }, [saveCurrentFileData, setSaveCallback]);

    useEffect(() => {
        if(isSaving || isInDrawingMode)
        {
            setCanSave(false);
            return;
        }
        
        if(dataContent instanceof KnownScaleContent) {
            setCanSave(true);
        }
        
        if(dataContent instanceof UserDefinedContent) {
            setCanSave(dataContent.scale != null);
        }        
    }, [isSaving, isInDrawingMode, dataContent]);

    const heading = useMemo<ProjectHeadingState>(
        () => ({
            title:                
                <div className='flex flex-row gap-x-1.5 items-center'>
                    <span className='font-semibold'>Echelle</span>
                    <span>:</span>
                    <span>{building?.name}</span>
                    <FontAwesomeIcon icon={faChevronRight} className='text-sm text-gray-600'/>
                    <span>{storey?.name}</span>
                    <FontAwesomeIcon icon={faChevronRight} className='text-sm text-gray-600'/>
                    <span className='text-gray-500'>{storeyFile?.name}</span>
                </div>,
            left:
                <div className='flex flex-row gap-x-2'>
                    <Button variant='simple' className='pointer-events-auto flex flex-row gap-y-2 cursor-pointer' disabled={isSaving || isInDrawingMode} onClick={() => {
                        const from = getFrom();
                        if(from) {
                            setRoute(from)
                        } else {
                            setRoute('organisation');
                        }
                    }}>
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </Button>

                    {projectLockStatus === ProjectLockStatus.LOCK_OWNED &&
                        <LoadingButton onClick={saveCurrentFileData}
                                       isLoading={isSaving} size="sm"
                                       disabled={!canSave}
                                       loadingBody='Sauvegarde en cours...' 
                                       body={<FontAwesomeIcon icon={faSave} />} />
                    }
                </div>
        }), [building?.name, storey?.name, storeyFile?.name, isSaving, isInDrawingMode, projectLockStatus, saveCurrentFileData, canSave, getFrom, setRoute]);

    useProjectFormHeadingLayoutEffect(heading);

    return (
        <ViewerContextProvider loaders={viewerLoaders} tools={viewerTools}>
            <div className="relative w-full h-full">
                <div className='absolute flex flex-row inset-4 z-1 gap-x-2 pointer-events-none'>
                    <PlanDataDetails ref={planDataDetailsRef} viewerRef={viewerRef} />
                </div>
                <OrganisationPlanViewerContainer ref={viewerRef} planDataDetailsRef={planDataDetailsRef} />
            </div>
        </ViewerContextProvider>
    );
}

export default ProjectOrganisationPlanDetails;